import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const toCents = (v) => {
  const s = String(v ?? "").trim();
  if (s === "") return null;
  const n = Number(s.replace(/[$,]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const toDollars = (c) => (c == null ? "" : (c / 100).toString());

export function AlbumDialog({ open, onOpenChange, artistId, album, onSaved }) {
  const editing = !!album;
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("album");
  const [tracks, setTracks] = useState("");
  const [masters, setMasters] = useState("");
  const [rec, setRec] = useState("");
  const [mkt, setMkt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(album?.title || "");
    setKind(album?.kind || "album");
    setTracks(album?.planned_tracks ?? "");
    setMasters(album?.masters ?? "");
    setRec(toDollars(album?.approved_recording_allocation_cents));
    setMkt(toDollars(album?.approved_marketing_allocation_cents));
  }, [open, album]);

  const submit = async () => {
    setSaving(true);
    const payload = {
      title: title.trim() || null,
      kind,
      planned_tracks: tracks === "" ? null : Number(tracks),
      masters: masters === "" ? null : Number(masters),
      approved_recording_allocation_cents: toCents(rec),
      approved_marketing_allocation_cents: toCents(mkt),
    };
    try {
      if (editing) await api.put(`/budget/albums/${album.id}`, payload);
      else await api.post(`/budget/artists/${artistId}/albums`, payload);
      toast.success(editing ? "Album updated" : "Album added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="album-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit album" : "Add album"}</DialogTitle>
          <DialogDescription>Approved allocations only — leave blank to show “Not allocated”.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5">
            <Label>Title</Label>
            <Input data-testid="album-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Cherry Paint" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="album-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="album">Album</SelectItem>
                  <SelectItem value="ep">EP</SelectItem>
                  <SelectItem value="lp">LP</SelectItem>
                  <SelectItem value="single">Singles</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Planned tracks</Label>
              <Input data-testid="album-tracks" type="number" value={tracks} onChange={(e) => setTracks(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Recording allocation ($)</Label>
              <Input data-testid="album-rec" value={rec} onChange={(e) => setRec(e.target.value)} placeholder="Not allocated" />
            </div>
            <div className="grid gap-1.5">
              <Label>Marketing allocation ($)</Label>
              <Input data-testid="album-mkt" value={mkt} onChange={(e) => setMkt(e.target.value)} placeholder="Not allocated" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid="album-save" onClick={submit} disabled={saving} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {editing ? "Save" : "Add album"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
